import React from 'react';
import { unescape, isEmpty } from 'lodash';
import get from 'lodash/get';

import { I18n } from 'common/i18n';
import { FeatureFlags } from '@socrata/platform/feature-flags';

import { NULL_GROUPING } from './Constants';
import { ValueRenderer as valueRenderer, IValueRendererProps } from './ValueRenderer';
import { shouldRenderAsHtml } from './helpers/TableColumnFormatting';
import type { CustomAgGridContext } from 'common/types/agGrid/context';
import { isTotalCell } from './helpers/isTotalCell';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';

const scope = 'shared.visualizations.charts';

const translations = {
  noValue: I18n.t('common.summary_table.no_value', { scope }),
  total: I18n.t('table.total', { scope }),
  netTotal: I18n.t('table.net_total', { scope }),
  subtotal: I18n.t('table.subtotal', { scope }),
};

export const GroupAndTotalRenderer = (props: IValueRendererProps) => {
  // Since props.data can be either TData or undefined, we want to skip rendering
  // if the dataset is empty or if there are zero results, as DataTypeFormatter expects a result.
  // Sample props.data = {date: '2021-01-30T00:00:00.000', id: '1'}
  if (isEmpty(props.data) && isTotalCell(props)) {
    return null;
  }

  const { showNetTotal, hierarchyConfig } = props.context as CustomAgGridContext;
  const totalLabel =
    FeatureFlags.value('enable_prototype_net_total') && showNetTotal
      ? translations.netTotal
      : translations.total;

  const footer = props.node.footer;
  const isRootLevel = props.node.level === -1;
  const columnMetadata = props.context.columnMetadata ?? [];

  let value = '';

  if (props.node.key === NULL_GROUPING) {
    value = translations.noValue;
  } else {
    // We are unescaping the value returned by valueRenderer
    // because AgGrid auto group will escape the value we pass, and we do not want to double escape
    // except URL or Email
    value = shouldRenderAsHtml(props.node, columnMetadata)
      ? valueRenderer(props)
      : unescape(valueRenderer(props));
  }

  const customSubtotalLabel = get(hierarchyConfig, 'customLabels.subtotals');
  // Currently, we don't need to show a custom label when exporting XLSX.
  const subTotalLabel =
    isTableAdvancedTotalingEnabled() && !isEmpty(customSubtotalLabel) && !props.forExport
      ? customSubtotalLabel
      : translations.subtotal;

  if (footer) {
    if (isRootLevel) {
      return <span>{totalLabel}</span>;
    } else {
      return (
        <span data-testid="subtotal-cell">
          {subTotalLabel} {value}
        </span>
      );
    }
  } else {
    return <span>{value}</span>;
  }
};
